import { createRef, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';

import { useStyles } from './styles';

import LoadingIndicator from '~/components/LoadingIndicator';

const REQUEST_TIMEOUT = 10000;
const BYTES_IN_MEGABYTE = 1048576;

const GoogleDocsViewer = ({
  fileUrl,
  fileSize,
  fileName,
  webviewType,
  onClose,
  'data-testid': dataTestId,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFailed, setIsLoadingFailed] = useState(false);
  const timeoutRef = createRef(null);
  const iframeRef = createRef(null);
  const styles = useStyles();

  const getIframeLink = useCallback(() => {
    return webviewType === 'office'
      ? `https://view.officeapps.live.com/op/embed.aspx?src=${window.encodeURIComponent(fileUrl)}`
      : `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
  }, [fileUrl]);

  const reloadFrame = () => {
    if (iframeRef.current) {
      iframeRef.current.src = getIframeLink();
      iframeRef.current.contentWindow.location.reload();
    }
  };

  const handleFrameLoad = () => {
    if (timeoutRef.current) {
      clearInterval(timeoutRef.current);
    }
    setIsLoading(false);
  };

  const handleFrameError = () => {
    handleFrameLoad();
    setIsLoadingFailed(true);
  };

  useEffect(() => {
    setIsLoading(true);
    setIsLoadingFailed(false);
    const timeOut =
      fileSize && fileSize >= BYTES_IN_MEGABYTE
        ? (fileSize / BYTES_IN_MEGABYTE) * REQUEST_TIMEOUT
        : REQUEST_TIMEOUT;

    if (timeoutRef) {
      clearInterval(timeoutRef.current);
      timeoutRef.current = setInterval(reloadFrame, timeOut);
    }

    return () => {
      clearInterval(timeoutRef.current);
    };
  }, [fileUrl]);

  return (
    <div data-testid={`${dataTestId}_file_${fileName}`} className={styles.viewer}>
      <div className={styles.toolbar}>
        <Typography data-testid={`${dataTestId}_fileName`}>{fileName}</Typography>
        <div>
          <IconButton size="small" className={styles.iconButton}>
            <a download href={fileUrl} target="_blank" rel="noopener noreferrer">
              <GetAppIcon fontSize="small" />
            </a>
          </IconButton>
          <IconButton size="small" onClick={onClose} className={styles.iconButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {isLoading && (
        <div className={styles.frame}>
          <LoadingIndicator white={false} />
        </div>
      )}
      {isLoadingFailed ? (
        <div className={styles.frame}>
          <Typography variant="body2" className={styles.failed}>
            Loading failed
          </Typography>
        </div>
      ) : (
        <iframe
          data-testid={`${dataTestId}_frame`}
          className={classNames(styles.frame, {
            [styles.hiddenFrame]: isLoading,
          })}
          title={fileUrl}
          src={getIframeLink()}
          onLoad={handleFrameLoad}
          onError={handleFrameError}
          ref={iframeRef}
        />
      )}
    </div>
  );
};

GoogleDocsViewer.propTypes = {
  fileUrl: PropTypes.string.isRequired,
  webviewType: PropTypes.oneOf(['google', 'office']),
  fileName: PropTypes.string,
  fileSize: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  'data-testid': PropTypes.string,
};

GoogleDocsViewer.defaultProps = {
  fileName: undefined,
  fileSize: undefined,
  webviewType: 'google',
  'data-testid': 'DocumentViewer',
};

export default GoogleDocsViewer;
